<template>
  <div class="fill-height">
    <!-- Header / Toolbar -->
    <v-toolbar dark flat color="transparent" class="mb-2">
      <v-toolbar-title class="text-h6 font-weight-bold mr-4 hidden-xs-only">
        {{ folderName }}
      </v-toolbar-title>

      <v-text-field
        v-model="localSearch"
        prepend-inner-icon="mdi-magnify"
        placeholder="Suchen..."
        dense
        filled
        rounded
        hide-details
        style="max-width: 400px"
        class="mr-2"
        @keyup.enter="$emit('search-documents')"></v-text-field>

      <v-spacer></v-spacer>

      <v-btn v-if="selectedItems.length > 0" color="primary" class="mr-2" @click="downloadSelected" title="Ausgewählte herunterladen">
        <v-icon left>mdi-download</v-icon>
        Download ({{ selectedItems.length }})
      </v-btn>
    </v-toolbar>

    <!-- Desktop View: Data Table -->
    <v-data-table
      dark
      sort-by="date"
      sort-desc
      v-model="selectedItems"
      show-select
      v-if="$vuetify.breakpoint.mdAndUp"
      :loading="loading"
      :items-per-page="50"
      :headers="headers"
      :items="documents"
      :search="localSearch"
      item-key="_id"
      class="elevation-0 transparent">
      <template v-slot:item="{ item, isSelected, select }">
        <tr
          :style="getRowStyle(item)"
          class="cursor-pointer hover-row"
          @click.stop="$emit('open-document-actions', item)"
          draggable="true"
          @dragstart="onDragStart($event, item)">
          <td>
            <v-simple-checkbox :value="isSelected" @input="select($event)"></v-simple-checkbox>
          </td>
          <td>
            <div style="min-width: 80px" class="d-flex align-center">
              <v-icon small class="mr-2">mdi-calendar</v-icon>
              <span :style="{ color: getDateColor(item), fontWeight: '500' }">
                {{ formatDate(item.date) }}
              </span>
            </div>
          </td>
          <td>
            <v-chip small :color="getIconColor(item)" text-color="white" class="font-weight-bold">
              <v-icon left small>{{ iconByStatus(item.approvalStatus) }}</v-icon>
              {{ getStatusText(item.approvalStatus) }}
            </v-chip>
          </td>
          <td class="font-weight-medium text-truncate" style="max-width: 300px">
            {{ item.title || "Kein Titel" }}
          </td>
          <td class="text-truncate" style="max-width: 200px">
            {{ item.invoiceIssuer ? item.invoiceIssuer.title : "-" }}
          </td>
          <td class="text-truncate" style="max-width: 150px">
            <div v-if="item.assignedUsers && item.assignedUsers.length > 0" class="d-flex align-center">
              <v-avatar v-for="user of item.assignedUsers" :key="user._id" size="24" color="grey darken-2" class="mr-2">
                <span class="white--text text-caption">
                  {{ getInitials(user.user) }}
                </span>
              </v-avatar>
            </div>
            <span v-else class="grey--text text--lighten-1">-</span>
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- Mobile View: Data Iterator (Cards) -->
    <v-data-iterator dark v-else :items="documents" :search="localSearch" :loading="loading" hide-default-footer class="pa-2">
      <template v-slot:default="{ items }">
        <v-row no-gutters>
          <v-col v-for="item in items" :key="item._id" cols="12">
            <v-card outlined @click="$emit('open-document-actions', item)" class="mb-2 rounded-lg">
              <v-list-item>
                <v-list-item-action>
                  <v-checkbox v-model="selectedItems" :value="item" @click.stop></v-checkbox>
                </v-list-item-action>
                <v-list-item-avatar>
                  <v-icon :color="getIconColor(item)" large>
                    {{ iconByStatus(item.approvalStatus) }}
                  </v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-subtitle class="caption mb-1">
                    {{ formatDate(item.date) }}
                  </v-list-item-subtitle>
                  <v-list-item-title style="max-width: 220px" class="font-weight-bold">
                    {{ item.title || "Kein Titel" }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ item.invoiceIssuer ? item.invoiceIssuer.title : "Kein Rechnungssteller" }}
                  </v-list-item-subtitle>

                  <v-list-item-subtitle v-if="item.assignedUsers && item.assignedUsers.length > 0" class="d-flex align-center">
                    <v-avatar :key="user._id" v-for="user of item.assignedUsers" size="24" color="grey darken-2" class="mr-2">
                      <span class="white--text text-caption">
                        {{ getInitials(user.user) }}
                      </span>
                    </v-avatar>
                  </v-list-item-subtitle>
                  <span v-else class="grey--text text--lighten-1">-</span>
                </v-list-item-content>
              </v-list-item>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </v-data-iterator>
  </div>
</template>

<script>
export default {
  name: "DocumentList",
  props: {
    documents: { type: Array, required: true },
    headers: { type: Array, required: true },
    folderName: { type: String, required: true },
    searchQuery: { type: String, required: true },
    loading: { type: Boolean, required: true },
  },
  data() {
    return {
      localSearch: this.searchQuery,
      selectedItems: [],
    }
  },
  watch: {
    searchQuery(newVal) {
      this.localSearch = newVal
    },
    localSearch(newVal) {
      this.$emit("update:searchQuery", newVal)
    },
  },
  methods: {
    getInitials(name) {
      let namee = `${name.vorname ? name.vorname : ""} ${name.nachname ? name.nachname : ""}`
      if (!namee) return "?"
      return namee
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2)
    },
    iconByStatus(status) {
      if (status === "signed") return "mdi-check-circle"
      if (status === "declined") return "mdi-close-circle"
      return "mdi-help-circle"
    },
    getStatusText(status) {
      if (status === "signed") return "Visiert"
      if (status === "declined") return "Abgelehnt"
      return "Offen"
    },
    getDateColor(item) {
      let status = item.approvalStatus
      if (status === "signed" || status === "declined") return "inherit"
      const diffDays = this.$moment().diff(this.$moment(item.date), "days")
      if (diffDays > 6) return "#E53935" // Red
      if (diffDays > 3) return "#FB8C00" // Orange
      return "inherit"
    },
    getIconColor(item) {
      let status = item.approvalStatus
      if (status === "signed") return "success"
      if (status === "declined") return "error"
      return "warning"
    },
    getRowStyle(item) {
      // Subtiles Highlighting für zugewiesene Dokumente
      if (item.userIsAssigned && item.approvalStatus !== "signed") {
        return "background-color: #474f55;" // Light Blue
      }
      return ""
    },
    onDragStart(e, item) {
      e.dataTransfer.setData("documentId", item._id)
      this.$emit("document-drag-start", e, item, this.selectedItems)
    },
    formatDate(date) {
      return date ? this.$moment(date).format("DD.MM.YY") : "-"
    },
    downloadSelected() {
      this.selectedItems.forEach((item) => {
        this.$emit("download-document", item)
      })
    },
  },
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
